import { useGetEntity, useGetSession } from '@opengov/bnp-core';
import { IEntity } from '@opengov/og-api-ts';
import { useMemo } from 'react';

import { ISessionUser } from '../../../../types';

/**
 * Entity + user for embed tile pickers (replaces compose-app selectors).
 *
 * The entity is the one being viewed (`entitySubdomain` from the route), not the session's home
 * entity: users with access to several entities must embed data from the entity whose story they
 * are editing. The entity is only fetched when it differs from the session entity.
 */
export function useEmbedSession(
  entitySubdomain?: string
): { entity: IEntity | undefined; user: ISessionUser | undefined } {
  const { data: session } = useGetSession();

  const sessionEntity = session?.entity as unknown as IEntity | undefined;
  const isOtherEntity =
    !!entitySubdomain && !!sessionEntity && entitySubdomain !== sessionEntity.subdomain && entitySubdomain !== sessionEntity.id;
  const { data: requestedEntity } = useGetEntity(isOtherEntity ? entitySubdomain : undefined);

  return useMemo(() => {
    if (!session) {
      return { entity: undefined, user: undefined };
    }

    const user: ISessionUser = {
      id: session.id,
      first_name: session.first_name,
      last_name: session.last_name,
      full_name: session.full_name,
      email: session.email,
      title: session.title ?? '',
      uuid: session.uuid
    };

    // While another entity is loading (or if it fails), return no entity rather than the session
    // entity, so pickers never show or embed data from the wrong entity.
    const entity = isOtherEntity ? (requestedEntity as unknown as IEntity | undefined) : sessionEntity;

    return { entity, user };
  }, [session, sessionEntity, isOtherEntity, requestedEntity]);
}
